import React from 'react';
import PropTypes from 'prop-types';
import { Subscribe } from 'unstated';
import { RefShape } from '../../shapes';
import Space from '../Space';
import ConciergeCard from '../ConciergeCard';
import SrpContainer from '../../containers/SrpContainer';
import { scrollToSpace } from '../../helpers/scrollTo';
import SpacePlaceholder from './SpacePlaceholder';
import Divider from './Divider';
import Summary from '../Pagination/Summary';

const Loader = () => (
  <div className='srp-loader'>
    <div className='loading' />
  </div>
);

const Placeholders = ({ length }) =>
  new Array(length).fill(undefined).map((_, i) => <SpacePlaceholder key={i} />);

const conciergeTags = [
  'Awards Ceremony',
  'events-awards-ceremony',
  'Christmas Party',
  'events-christmas-party',
  'Xmas Party',
  'events-xmas-party',
  'Conference',
  'meetings-conference',
  'Conference Venue',
  'venuetype-conference-venue',
  'Conference Centre',
  'venuetype-conference-centre',
  'Conference Room',
  'venuetype-conference-room',
  'Summer Party',
  'events-summer-party',
  'Corporate Team Away Day',
  'meetings-corporate-team-away-day',
  'Drinks Reception',
  'events-drinks-reception',
  'Office Party',
  'celebrations-office-party',
  'Team Building',
  'meetings-team-building',
  'Team Activity',
  'events-team-activity',
  'Team Away Day',
  'meetings-team-away-day',
  'Wedding',
  'celebrations-wedding',
  'Asian Wedding',
  'celebrations-asian-wedding',
  'Gay Wedding',
  'celebrations-gay-wedding',
  'Wedding Reception',
  'celebrations-wedding-reception',
  'Wedding Ceremony',
  'celebrations-wedding-ceremony',
  'Unusual Wedding',
  'celebrations-unusual-wedding',
];

let wantsToScrollTo = false;
export class Spaces extends React.Component {
  static propTypes = {
    spaces: PropTypes.object.isRequired,
    page: PropTypes.number.isRequired,
    isAdmin: PropTypes.bool.isRequired,
    superLoggedIn: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    selectedSpaceID: PropTypes.number,
    deselectSpace: PropTypes.func.isRequired,
    loadPage: PropTypes.func.isRequired,
    container: RefShape.isRequired,
    international: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      conciergeCard: this.conciergeCardShow(props.filters),
    };
  }

  conciergeCardShow = filters => {
    const checkIfConciergeTag = filters => {
      for (let i = 0; i < filters?.type?.length; i++) {
        if (conciergeTags.includes(filters.type[i])) return true;
      }
      return false;
    };

    return !!(
      (filters?.budget ? filters.budget[1] >= 4000 : false) ||
      conciergeTags.includes(filters?.tags) ||
      checkIfConciergeTag(filters)
    );
  };

  componentDidUpdate(prevProps, prevState, snapshot) {
    const conciergeCardShow = this.conciergeCardShow(this.props.filters);
    const { conciergeCard } = this.state;

    if (!conciergeCard && conciergeCardShow) {
      this.setState({ conciergeCard: true });
    }

    if (conciergeCard && !conciergeCardShow) {
      this.setState({ conciergeCard: false });
    }

    if (!window || !document) return; // for server side rendering
    if (
      !wantsToScrollTo &&
      prevProps.selectedSpaceID === this.props.selectedSpaceID
    )
      return;
    wantsToScrollTo = true;
    const container = this.props.container.current;
    if (!container) return;
    wantsToScrollTo = false;

    scrollToSpace(container, this.props.selectedSpaceID);
  }

  componentDidMount() {
    if (typeof window === 'undefined') return;
    window.addEventListener('click', this.handleClick);
    const lastClickedSpace =
      window.history.state && window.history.state.srpClickedSpace;
    // console.log({ lastClickedSpace })
    if (typeof window !== 'undefined' && lastClickedSpace) {
      // setTimeout to wait for it to mount
      // console.log({ scrollto: this.props.selectedSpaceID })
      setTimeout(async () => {
        await scrollToSpace(
          this.props.container.current,
          lastClickedSpace,
          true,
        );
      }, 10);
    }
  }

  componentWillUnmount() {
    if (!window) return;
    window.removeEventListener('click', this.handleClick);
  }

  handleClick = e => {
    if (!this.el.contains(e.target)) return;
    this.props.deselectSpace();
  };

  render() {
    const {
      spaces,
      isAdmin,
      superLoggedIn,
      isLoading,
      selectedSpaceID,
      loadPage,
      international = false,
    } = this.props;
    const pages = Object.entries(spaces).sort(([a], [b]) => a - b);
    const { conciergeCard } = this.state;

    return (
      <div className='srp-results-pages' ref={ref => (this.el = ref)}>
        {isLoading && <Loader />}

        {pages.map(([page, spaces], i) => {
          page = parseInt(page);
          const nextPage = pages[i + 1] && parseInt(pages[i + 1][0], 10);
          const shouldShowDivider = nextPage && nextPage > page + 1;

          return (
            <div className='srp-page-container' data-page={page} key={page}>
              <ConciergeCard conciergeCase={conciergeCard} />
              {spaces.map(s => (
                <Space
                  key={s.id}
                  space={s}
                  isAdmin={isAdmin}
                  superLoggedIn={superLoggedIn}
                  ga='srp_space_tile'
                  isSelected={selectedSpaceID === s.id}
                  includeSrpFilters
                  isReducedSpace={international}
                />
              ))}

              {shouldShowDivider &&
                (isLoading ? (
                  <Placeholders length={36} />
                ) : (
                  <Divider
                    skipStart={page}
                    skipEnd={nextPage}
                    loadPage={loadPage}
                  />
                ))}
            </div>
          );
        })}

        <Summary />
      </div>
    );
  }
}

const SpacesContainer = ({ container }) => (
  <Subscribe to={[SrpContainer]}>
    {({
      state: {
        data,
        isAdmin,
        superLoggedIn,
        isLoading,
        selectedSpaceID,
        international,
      },
      deselectSpace,
      loadPage,
      filters,
    }) => (
      <Spaces
        container={container}
        spaces={data.spaces}
        page={data.page}
        isAdmin={isAdmin}
        superLoggedIn={superLoggedIn}
        isLoading={isLoading}
        selectedSpaceID={selectedSpaceID}
        deselectSpace={deselectSpace}
        loadPage={loadPage}
        international={international}
        filters={filters}
      />
    )}
  </Subscribe>
);

SpacesContainer.propTypes = {
  container: RefShape.isRequired,
};

export default SpacesContainer;
